import ReactSelect from 'components/base/ReactSelect';
import useDebounce from 'hooks/useDebounce';
import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Form } from 'react-bootstrap';
import axiosInstance from 'utils/axiosInterceptors';
import { TSupplierDetails } from 'utils/types';

export type MovePurchaseOrderItemFormHandle = {
  getSupplierId: () => number | null;
};

type SelectOption = {
  label: string;
  value: number;
};

const MovePurchaseOrderItemForm = forwardRef<MovePurchaseOrderItemFormHandle>(
  (_, ref) => {
    const [suppliers, setSuppliers] = useState<TSupplierDetails[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [supplierId, setSupplierId] = useState<number | null>(null);
    const [input, setInput] = useState('');
    const debouncedInput = useDebounce(input, 500);

    const fetchSupplier = async (search: string = '') => {
      setIsLoading(true);
      try {
        const res = await axiosInstance.get(
          `/api/v1/suppliers?page=1&pageSize=10&search=${search}`
        );
        setSuppliers(res.data.data);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };

    const formattedSupplierOptions = suppliers?.map(supplier => ({
      value: supplier.supplierId,
      label: supplier.name
    }));

    useImperativeHandle(ref, () => ({
      getSupplierId: () => supplierId
    }));

    useEffect(() => {
      if (debouncedInput) {
        fetchSupplier(debouncedInput);
      } else {
        fetchSupplier();
      }
    }, [debouncedInput]);

    return (
      <Form>
        <Form.Group>
          <Form.Label>Move remaining quantity to supplier</Form.Label>
          <ReactSelect
            placeholder="Select supplier"
            options={formattedSupplierOptions}
            onChange={newvalue =>
              setSupplierId((newvalue as SelectOption).value)
            }
            closeMenuOnSelect
            isLoading={isLoading}
            isSearchable
            onInputChange={newValue => setInput(newValue)}
          />
          <Form.Text className="text-body-tertiary">
            Adds the remaining quantity to that supplier&apos;s latest open
            purchase order, or creates a new one.
          </Form.Text>
        </Form.Group>
      </Form>
    );
  }
);

MovePurchaseOrderItemForm.displayName = 'MovePurchaseOrderItemForm';

export default MovePurchaseOrderItemForm;
